import { IncomeMode, OwnerPayConfig } from "../types.ts";

/**
 * UI copy + visibility flags that depend on how the user's income arrives. Kept
 * out of the pure engine (cashflow.ts) so the engine stays string-free.
 *
 * - "irregular" / "mixed": the freelancer framing — owner's pay, a smoothing
 *   buffer, a tax set-aside, expected/invoiced income.
 * - "regular": the same engine, spoken as a plain monthly budget — a spending
 *   plan, an emergency fund, no tax reserve, no lean-month card.
 */

interface ModeLabels {
  planCardTitle: string;
  safeAmountLabel: string;
  currentAmountLabel: string;
  currentAmountField: string;
  bufferLabel: string;
  bufferSubLabel: string;
  contributionInVerb: string;
  contributionOutVerb: string;
  ownerPayPageTitle: string;
  ownerPayPageIntro: string;
  accountLinkLabel: string;
  accountLinkSub: string;
  showTaxCard: boolean;
  showLeanCard: boolean;
  showExpectedIncome: boolean;
}

const isRegular = (mode: IncomeMode) => mode === "regular";

export const modeLabels = (config: OwnerPayConfig): ModeLabels => {
  const regular = isRegular(config.mode);

  return {
    planCardTitle: regular ? "Monthly spending plan" : "Owner's pay",
    safeAmountLabel: regular
      ? "Safe to spend each month"
      : "Safe to draw / month",
    currentAmountLabel: regular
      ? "Your monthly budget"
      : "You currently pay yourself",
    currentAmountField: regular
      ? "What you plan to spend / month"
      : "What you pay yourself / month",
    bufferLabel: regular ? "Emergency fund" : "Buffer",
    bufferSubLabel: regular ? "Savings for a rainy day" : "Smoothing reserve",
    contributionInVerb: regular ? "to savings" : "to buffer",
    contributionOutVerb: regular ? "from savings" : "from buffer",
    ownerPayPageTitle: regular ? "Spending Plan" : "Owner's Pay",
    ownerPayPageIntro: regular
      ? "Set a monthly budget you can keep, and how big an emergency fund you want to build."
      : "Instead of spending whatever landed this month, pay yourself a steady wage from the buffer. Pick how conservative that wage should be and how much of every payment to hold back for taxes.",
    accountLinkLabel: regular ? "Spending Plan" : "Owner's Pay",
    accountLinkSub: regular
      ? "Set your monthly budget and emergency-fund goal"
      : "Set how you pay yourself and your tax reserve",
    showTaxCard: !regular || config.taxReservePercent > 0,
    showLeanCard: !regular,
    showExpectedIncome: !regular,
  };
};

export const INCOME_MODE_OPTIONS: {
  value: IncomeMode;
  title: string;
  blurb: string;
}[] = [
  {
    value: "regular",
    title: "A regular paycheck",
    blurb: "Same amount, same schedule. Salaried or steady hourly work.",
  },
  {
    value: "irregular",
    title: "Irregular or self-employed",
    blurb:
      "Freelance, agency, consulting, commission — income arrives in uneven lumps.",
  },
  {
    value: "mixed",
    title: "A mix of both",
    blurb: "A steady paycheck plus freelance or side income on top.",
  },
];
